import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Play, CheckCircle2, XCircle, Terminal, Clock, ChevronDown, Archive, ArchiveRestore } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const PAGE_SIZE = 20;

const MaintenanceTab = () => {
  const [scripts, setScripts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState(null);
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [scriptsRes, logsRes] = await Promise.all([
        api.get('/maintenance/scripts'),
        api.get('/maintenance/logs'),
      ]);
      setScripts(scriptsRes.data || []);
      setLogs(logsRes.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const runScript = async (key) => {
    setRunning(key);
    setOutput(null);
    try {
      const res = await api.post(`/maintenance/run/${key}`);
      setOutput({ key, status: res.data.status, text: res.data.output });
      if (res.data.status === 'success') toast.success('Script completed');
      else toast.error('Script failed');
      fetchData();
    } catch {
      toast.error('Failed to run script');
    } finally {
      setRunning(null);
    }
  };

  const toggleArchive = async (key, currentlyArchived) => {
    // Optimistic update
    setScripts(prev => prev.map(s => s.key === key ? { ...s, is_archived: !currentlyArchived } : s));
    try {
      await api.patch(`/maintenance/scripts/${key}/archive`, { archived: !currentlyArchived });
      toast.success(currentlyArchived ? 'Script restored' : 'Script archived');
    } catch {
      // Revert on failure
      setScripts(prev => prev.map(s => s.key === key ? { ...s, is_archived: currentlyArchived } : s));
      toast.error('Failed to update archive status');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const activeScripts   = scripts.filter(s => !s.is_archived);
  const archivedScripts = scripts.filter(s => s.is_archived);
  const displayScripts  = showArchived ? archivedScripts : activeScripts;
  const visibleScripts  = displayScripts.slice(0, visibleCount);
  const hasMore         = displayScripts.length > visibleCount;

  return (
    <div className="space-y-6">
      {/* Available Scripts */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Terminal className="w-5 h-5" /> Maintenance Scripts
              <Badge variant="outline" className="text-xs ml-1">
                {showArchived ? archivedScripts.length : activeScripts.length}
              </Badge>
            </CardTitle>
            {archivedScripts.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="text-xs gap-1.5 text-muted-foreground"
                onClick={() => { setShowArchived(v => !v); setVisibleCount(PAGE_SIZE); }}
              >
                {showArchived
                  ? <><ArchiveRestore className="w-3.5 h-3.5" /> Show Active ({activeScripts.length})</>
                  : <><Archive className="w-3.5 h-3.5" /> Show Archived ({archivedScripts.length})</>}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {showArchived
              ? 'Archived scripts — click the restore icon to make them active again.'
              : 'Pre-approved scripts for data maintenance and migrations. Archive scripts you no longer need to declutter the list.'}
          </p>

          <div className={`space-y-2 ${displayScripts.length > PAGE_SIZE ? 'max-h-[520px] overflow-y-auto pr-1' : ''}`}>
            {visibleScripts.map(s => (
              <div key={s.key} className={`border rounded p-3 flex items-center justify-between gap-2 ${showArchived ? 'opacity-60' : ''}`}>
                <div className="flex-1 min-w-0 mr-1">
                  <p className="text-sm font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground line-clamp-2">{s.description}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {!showArchived && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runScript(s.key)}
                      disabled={running === s.key}
                    >
                      {running === s.key
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Play className="w-4 h-4 mr-1" />}
                      {running === s.key ? 'Running...' : 'Run'}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-8 h-8 p-0 text-muted-foreground hover:text-foreground"
                    onClick={() => toggleArchive(s.key, s.is_archived)}
                    title={showArchived ? 'Restore script' : 'Archive script'}
                    data-testid={`archive-script-${s.key}`}
                  >
                    {showArchived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>
            ))}
            {displayScripts.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                {showArchived ? 'No archived scripts.' : 'No active scripts.'}
              </p>
            )}
          </div>

          {hasMore && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-muted-foreground"
              onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
            >
              <ChevronDown className="w-4 h-4 mr-1" />
              Load more ({displayScripts.length - visibleCount} remaining)
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Script Output */}
      {output && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              {output.status === 'success'
                ? <CheckCircle2 className="w-4 h-4 text-green-600" />
                : <XCircle className="w-4 h-4 text-red-600" />}
              Script Output
              <Badge className={output.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                {output.status}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted/50 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-60 overflow-y-auto font-mono">
              {output.text || 'No output'}
            </pre>
          </CardContent>
        </Card>
      )}

      {/* Recent Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" /> Recent Runs
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No scripts have been run yet.</p>
          ) : (
            <div className="space-y-2">
              {logs.slice(0, 20).map(log => (
                <div key={log.id} className="flex items-center gap-3 text-sm border-b pb-2 last:border-b-0">
                  {log.status === 'success'
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                    : <XCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />}
                  <span className="font-medium flex-1">{log.script_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {log.run_at ? new Date(log.run_at).toLocaleString() : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MaintenanceTab;
